/**
 * Identifica lacune nel programma di allenamento
 * @param {Array} workouts - Array di allenamenti
 * @param {Array} sets - Array di serie
 * @param {Object} userProfile - Profilo dell'utente
 * @returns {Object} Analisi delle lacune nel programma
 */
export function identifyTrainingGaps(workouts, sets, userProfile = {}) {
    // Risultati dell'analisi
    const results = {
      missingElements: [],
      suboptimalPatterns: [],
      programRecommendations: []
    };
    
    // Analizza gruppi muscolari allenati
    const muscleGroupFrequency = analyzeMuscleTrained(sets);
    
    // Analizza tipi di esercizi usati
    const exerciseTypeAnalysis = analyzeExerciseTypes(sets);
    
    // Analizza la frequenza di allenamento
    const workoutsPerWeek = calculateWorkoutsPerWeek(workouts);
    
    // Identifica elementi mancanti nel programma
    identifyMissingTrainingElements(
      muscleGroupFrequency, 
      exerciseTypeAnalysis, 
      workoutsPerWeek, 
      results
    );
    
    // Identifica pattern non ottimali
    identifySuboptimalPatterns(
      workouts, 
      sets, 
      muscleGroupFrequency, 
      workoutsPerWeek, 
      results
    );
    
    // Genera raccomandazioni programmatiche
    results.programRecommendations = generateProgramRecommendations(
      results.missingElements,
      results.suboptimalPatterns,
      userProfile
    );
    
    return results;
  }
  
  /**
   * Analizza la frequenza dei gruppi muscolari allenati
   * @param {Array} sets - Array di serie
   * @returns {Object} Frequenza di allenamento per gruppo muscolare
   */
  function analyzeMuscleTrained(sets) {
    const muscleGroupFrequency = {};
    
    sets.forEach(set => {
      const muscleGroup = set.gruppo_muscolare || 'Altro';
      
      if (!muscleGroupFrequency[muscleGroup]) {
        muscleGroupFrequency[muscleGroup] = 0;
      }
      
      muscleGroupFrequency[muscleGroup]++;
    });
    
    return muscleGroupFrequency;
  }
  
  /**
   * Analizza i tipi di esercizi utilizzati
   * @param {Array} sets - Array di serie
   * @returns {Object} Analisi dei tipi di esercizi
   */
  function analyzeExerciseTypes(sets) {
    const exerciseTypes = {
      compound: 0,
      isolation: 0,
      machine: 0,
      bodyweight: 0,
      total: 0
    };
    
    sets.forEach(set => {
      const exerciseName = set.esercizio_nome?.toLowerCase() || '';
      const weight = parseFloat(set.peso) || 0;
      
      exerciseTypes.total++;
      
      // Esercizi a corpo libero
      if (weight === 0 && !isIsometric(exerciseName)) {
        exerciseTypes.bodyweight++;
        return;
      }
      
      // Esercizi composti
      if (
        exerciseName.includes('squat') ||
        exerciseName.includes('stacco') ||
        exerciseName.includes('panca') ||
        exerciseName.includes('military') ||
        exerciseName.includes('trazioni') ||
        exerciseName.includes('deadlift') ||
        exerciseName.includes('press')
      ) {
        exerciseTypes.compound++;
        return;
      }
      
      // Esercizi con macchine
      if (
        exerciseName.includes('machine') ||
        exerciseName.includes('macchina') ||
        exerciseName.includes('lat') ||
        exerciseName.includes('cavi') ||
        exerciseName.includes('cable')
      ) {
        exerciseTypes.machine++;
        return;
      }
      
      // Di default, consideralo isolamento
      exerciseTypes.isolation++;
    });
    
    return exerciseTypes;
  }
  
  /**
   * Verifica se un esercizio è isometrico
   * @param {string} exerciseName - Nome dell'esercizio
   * @returns {boolean} True se l'esercizio è isometrico
   */
  function isIsometric(exerciseName) {
    return (
      exerciseName.includes('plank') ||
      exerciseName.includes('hold') ||
      exerciseName.includes('isometric') ||
      exerciseName.includes('wall sit')
    );
  }
  
  /**
   * Calcola la frequenza di allenamento settimanale
   * @param {Array} workouts - Array di allenamenti
   * @returns {number} Media di allenamenti per settimana
   */
  function calculateWorkoutsPerWeek(workouts) {
    if (workouts.length < 2) return workouts.length;
    
    // Ordina gli allenamenti per data
    const sortedWorkouts = [...workouts].sort(
      (a, b) => new Date(a.data_allenamento) - new Date(b.data_allenamento)
    );
    
    // Calcola le settimane tra il primo e l'ultimo allenamento
    const firstDate = new Date(sortedWorkouts[0].data_allenamento);
    const lastDate = new Date(sortedWorkouts[sortedWorkouts.length - 1].data_allenamento);
    const daysDiff = Math.max(1, Math.round((lastDate - firstDate) / (24 * 60 * 60 * 1000)));
    const weeksDiff = Math.max(1, daysDiff / 7);
    
    return parseFloat((workouts.length / weeksDiff).toFixed(1));
  }
  
  /**
   * Identifica elementi mancanti nel programma di allenamento
   * @param {Object} muscleGroupFrequency - Frequenza di allenamento per gruppo muscolare
   * @param {Object} exerciseTypeAnalysis - Analisi dei tipi di esercizi
   * @param {number} workoutsPerWeek - Media di allenamenti per settimana
   * @param {Object} results - Oggetto risultati da popolare
   */
  function identifyMissingTrainingElements(muscleGroupFrequency, exerciseTypeAnalysis, workoutsPerWeek, results) {
    const keyMuscleGroups = ['Petto', 'Schiena', 'Gambe', 'Spalle'];
    
    // Verifica gruppi muscolari mancanti
    keyMuscleGroups.forEach(group => {
      if (!muscleGroupFrequency[group]) {
        results.missingElements.push({
          title: `${group} non allenato`,
          description: `Non hai allenato il ${group}, un gruppo muscolare importante per uno sviluppo fisico equilibrato.`,
          importance: 'high'
        });
      }
    });
    
    // Verifica percentuale di esercizi composti
    if (exerciseTypeAnalysis.total > 10) {
      const compoundPercentage = (exerciseTypeAnalysis.compound / exerciseTypeAnalysis.total) * 100;
      
      if (compoundPercentage < 30) {
        results.missingElements.push({
          title: 'Pochi esercizi composti',
          description: 'Gli esercizi composti sono essenziali per lo sviluppo di forza e massa. Il tuo programma ne include pochi.',
          importance: 'medium'
        });
      }
    }
    
    // Verifica frequenza di allenamento
    if (workoutsPerWeek < 2) {
      results.missingElements.push({
        title: 'Frequenza di allenamento bassa',
        description: 'Allenarti almeno 2-3 volte a settimana è consigliato per ottenere risultati costanti.',
        importance: 'medium'
      });
    }
  }
  
  /**
   * Identifica pattern non ottimali nel programma di allenamento
   * @param {Array} workouts - Array di allenamenti
   * @param {Array} sets - Array di serie
   * @param {Object} muscleGroupFrequency - Frequenza di allenamento per gruppo muscolare
   * @param {number} workoutsPerWeek - Media di allenamenti per settimana
   * @param {Object} results - Oggetto risultati da popolare
   */
  function identifySuboptimalPatterns(workouts, sets, muscleGroupFrequency, workoutsPerWeek, results) {
    // Verifica squilibri tra gruppi muscolari
    const muscleGroups = Object.entries(muscleGroupFrequency)
      .filter(([name]) => name !== 'Altro')
      .sort((a, b) => b[1] - a[1]);
    
    if (muscleGroups.length >= 2) {
      const mostTrainedGroup = muscleGroups[0];
      const leastTrainedGroup = muscleGroups[muscleGroups.length - 1];
      
      if (mostTrainedGroup[1] > leastTrainedGroup[1] * 3) {
        results.suboptimalPatterns.push({
          title: 'Squilibrio tra gruppi muscolari',
          description: `Alleni ${mostTrainedGroup[0]} molto più di ${leastTrainedGroup[0]}, creando uno squilibrio.`,
          suggestions: [
            'Dedica più attenzione ai gruppi muscolari meno allenati',
            `Aggiungi esercizi specifici per ${leastTrainedGroup[0]} nel tuo programma settimanale`
          ]
        });
      }
    }
    
    // Verifica variazione esercizi
    const exerciseCounts = {};
    sets.forEach(set => {
      if (!set.esercizio_nome) return;
      exerciseCounts[set.esercizio_nome] = (exerciseCounts[set.esercizio_nome] || 0) + 1;
    });
    
    // Verifica se c'è poca variazione negli esercizi
    const totalExercises = Object.keys(exerciseCounts).length;
    if (totalExercises < 5 && sets.length > 15) {
      results.suboptimalPatterns.push({
        title: 'Poca variazione negli esercizi',
        description: 'Utilizzi un numero limitato di esercizi. Aggiungere più variazione può migliorare i risultati e prevenire plateau.',
        suggestions: [
          'Introduce 1-2 nuovi esercizi ogni 4-6 settimane',
          'Usa varianti degli stessi esercizi (es. panca inclinata invece che piana)',
          'Alterna tra manubri, bilancieri e macchine per lo stesso gruppo muscolare'
        ]
      });
    }
    
    // Verifica alta frequenza con recupero insufficiente
    if (workoutsPerWeek > 4) {
      // Conta gli allenamenti negli ultimi 7 giorni
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      
      const recentWorkouts = workouts.filter(w => 
        new Date(w.data_allenamento) >= oneWeekAgo
      );
      
      if (recentWorkouts.length > 5) {
        results.suboptimalPatterns.push({
          title: 'Frequenza elevata senza recupero adeguato',
          description: 'Ti alleni quasi tutti i giorni. Il recupero è essenziale per la crescita muscolare e le prestazioni.',
          suggestions: [
            'Inserisci almeno 2-3 giorni di riposo completo a settimana',
            'Considera di alternare allenamenti intensi e leggeri',
            'Pianifica una settimana di scarico ogni 4-6 settimane di allenamento intenso'
          ]
        });
      }
    }
  }
  
/**
 * Genera raccomandazioni per il programma di allenamento
 * @param {Array} missingElements - Elementi mancanti nel programma
 * @param {Array} suboptimalPatterns - Pattern non ottimali
 * @param {Object} userProfile - Profilo dell'utente
 * @returns {Array} Raccomandazioni per il programma
 */
function generateProgramRecommendations(missingElements, suboptimalPatterns, userProfile = {}) {
  const recommendations = [];
  
  // Raccomandazioni in base agli elementi mancanti
  if (missingElements.some(el => el.title.includes('Pochi esercizi composti'))) {
      recommendations.push({
          title: 'Aggiungi esercizi composti',
          description: 'Gli esercizi composti attivano più muscoli contemporaneamente e stimolano maggiore crescita.',
          implementation: [
              'Includi almeno 1-2 esercizi composti (squat, stacco, panca, trazioni) in ogni allenamento',
              'Posiziona gli esercizi composti all\'inizio dell\'allenamento quando sei più fresco'
          ]
      });
  }
  
  if (missingElements.some(el => /non allenato/.test(el.title))) {
      // Trova i gruppi muscolari non allenati
      const missingMuscles = missingElements
          .filter(el => /non allenato/.test(el.title))
          .map(el => el.title.split(' ')[0]);
      
      recommendations.push({
          title: 'Bilanciamento muscolare',
          description: `Aggiungi allenamenti per ${missingMuscles.join(', ')} per uno sviluppo fisico equilibrato.`,
          implementation: [
              'Dedica un giorno di allenamento settimanale specifico per questi gruppi muscolari',
              'Aggiungi 2-3 esercizi per ciascun gruppo muscolare mancante'
          ]
      });
  }
  
  // Raccomandazioni in base ai pattern non ottimali
  if (suboptimalPatterns.some(el => el.title.includes('Squilibrio tra gruppi muscolari'))) {
      recommendations.push({
          title: 'Equilibra il tuo programma',
          description: 'Un programma bilanciato previene infortuni e migliora l\'estetica fisica.',
          implementation: [
              'Suddividi l\'allenamento in push (petto, spalle, tricipiti), pull (schiena, bicipiti) e legs (gambe)',
              'Dedica volumi simili a gruppi muscolari opposti (es. petto/schiena, bicipiti/tricipiti)'
          ]
      });
  }
  
  // Raccomandazione generale se l'utente è principiante
  // Nota: Verifica che userProfile esista prima di accedere alle sue proprietà
  if ((userProfile && userProfile.experienceLevel === 'beginner') || 
      (missingElements.length > 0 && suboptimalPatterns.length > 0)) {
      recommendations.push({
          title: 'Struttura un programma settimanale chiaro',
          description: 'Un programma ben strutturato è fondamentale per ottenere progressi costanti.',
          implementation: [
              'Pianifica 3-4 allenamenti a settimana con giorni di riposo adeguati',
              'Alterna allenamenti per parti superiori e inferiori del corpo',
              'Traccia i tuoi progressi aumentando gradualmente pesi o ripetizioni'
          ]
      });
  }
  
  return recommendations;
}